import { StyleSheet } from 'aphrodite';
import {
	antiFlashWhite,
	background,
	borderColor,
	brandPrimaryActive,
	brandPrimaryHover,
	destructive,
	destructiveActive,
	destructiveHover,
	destructiveHoverLight,
	shuffleGray,
	titles,
	white,
} from '../../styles/colors';
import { shadowSm } from '../../styles/shadows';
import { CssSelectors } from '../../styles/styles';
import { THEME } from '../Theme/theme';

export const s = StyleSheet.create({
	base: {
		transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
		alignItems: 'center',
		borderRadius: 8,
		display: 'flex',
		fontSize: 14,
		fontFamily: THEME.typography.fontFamilies.lexend,
		justifyContent: 'center',
		textAlign: 'center',
		gap: 8,
		boxSizing: 'border-box',
		color: 'inherit',
		textDecoration: 'none',
		whiteSpace: 'nowrap',
		':has(*) > svg': {
			display: 'block',
			pointerEvents: 'none',
			flexShrink: 0,
			width: 16,
			height: 16,
		},
		':disabled': {
			pointerEvents: 'none',
			opacity: 0.5,
		},
		// The shared spinner paints its moving segment in Levitate blue (the account's button color on
		// public pages), which is the fill it sits on inside a filled button — leaving a rotating gap
		// instead of a rotating arc, or nothing at all when the fill is pale. currentColor is the
		// button's own text color, chosen to contrast with its fill whatever the variant.
		...CssSelectors.allDescendants(
			{
				borderColor: 'transparent',
				borderTopColor: 'currentColor',
			},
			'.loading-spinner-spinner'
		),
	},
	primary: {
		// --custom-* vars are set only by the injected page theme on public pages; fallbacks apply elsewhere.
		color: `var(--custom-primary-button-text, ${white})`,
		background: `var(--custom-primary-button-bg, ${THEME.colors.primary.main})`,
		border: 'none',
		boxShadow: '0 1px 2px 0 rgba(140,140,140,0.50)',
		':active': {
			// Tracks the custom hover color intentionally (no separate pressed color is collected).
			background: `var(--custom-primary-button-hover, ${brandPrimaryActive})`,
		},
		':hover': {
			background: `var(--custom-primary-button-hover, ${brandPrimaryHover})`,
		},
	},
	secondary: {
		color: THEME.colors.primary.main,
		background: white,
		border: `1px solid ${THEME.colors.primary.main}`,
		boxShadow: '0 1px 2px 0 #f9f9f9',
		':active': {
			background: '#E6ECF3',
		},
		':hover': {
			background: '#EAF7FA',
			border: `1px solid ${brandPrimaryHover}`,
		},
	},
	success: {
		color: THEME.colors.success.contrastText,
		background: THEME.colors.success.main,
		border: 'none',
		boxShadow: '0 1px 2px 0 rgba(140,140,140,0.50)',
		':active': {
			background: `oklch(from ${THEME.colors.success.main} calc(l - 0.08) c h)`,
		},
		':hover': {
			background: `oklch(from ${THEME.colors.success.main} calc(l + 0.06) c h)`,
		},
	},
	link: {
		color: THEME.colors.primary.main,
		background: 'transparent',
		border: 'none',
		boxShadow: 'none',
		':active': {
			color: brandPrimaryHover,
		},
		':hover': {
			color: brandPrimaryHover,
			textDecoration: 'underline',
		},
		':focus': {
			outline: 'none',
			color: brandPrimaryHover,
			textDecoration: 'underline',
		},
	},
	destructive: {
		color: white,
		background: destructive,
		border: 'none',
		boxShadow: '0 1px 2px 0 #f9f9f9',
		':active': {
			background: destructiveActive,
		},
		':hover': {
			background: destructiveHover,
		},
	},
	'destructive-secondary': {
		color: destructive,
		background: white,
		border: `1px solid ${destructive}`,
		boxShadow: '0 1px 2px 0 #f9f9f9',
		':active': {
			background: destructiveHoverLight,
			border: `1px solid ${destructiveActive}`,
			color: destructiveHover,
		},
		':hover': {
			background: destructiveHoverLight,
			border: `1px solid ${destructiveHover}`,
			color: destructiveHover,
		},
	},
	ghost: {
		':hover': {
			background: antiFlashWhite,
		},
	},
	outline: {
		border: `1px solid ${shuffleGray}`,
		background: white,
		':hover': {
			backgroundColor: background,
			color: titles,
		},
		':is([aria-expanded="true"])': {
			backgroundColor: background,
			color: titles,
		},
	},
	default: {
		height: 36,
		padding: '10px 16px',
		':has(> svg)': {
			paddingLeft: 12,
			paddingRight: 12,
		},
	},
	sm: {
		height: 32,
		paddingLeft: 12,
		paddingRight: 12,
		gap: 6,
		':has(> svg)': {
			paddingLeft: 10,
			paddingRight: 10,
		},
	},
	lg: {
		height: 40,
		paddingLeft: 24,
		paddingRight: 24,
		':has(> svg)': {
			paddingLeft: 16,
			paddingRight: 16,
		},
	},
	icon: {
		width: 24,
		height: 24,
		':has(*) > svg': {
			width: 16,
			height: 16,
		},
	},
	selectButton: {
		boxSizing: 'border-box',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		whiteSpace: 'nowrap',
		borderRadius: 6,
		border: `1px solid ${borderColor}`,
		backgroundColor: white,
		padding: `8px 12px`,
		fontSize: '0.875rem',
		lineHeight: '1.25rem',
		boxShadow: shadowSm,
		color: titles,
		':focus': {
			outline: 'none',
		},
		':disabled': {
			cursor: 'not-allowed',
			opacity: 0.5,
		},
		':has(*) > span': {
			overflow: 'hidden',
			display: '-webkit-box',
			'-webkit-box-orient': 'vertical',
			'-webkit-line-clamp': 1,
		},
	},
	selectButtonChevron: {
		display: 'block',
		boxSizing: 'border-box',
		opacity: 0.5,
		height: 16,
		width: 16,
		flexShrink: 0,
		pointerEvents: 'none',
	},
});

export const StandardButtonStyles = s;
